// Statistics behind the node insights panel. Pure functions, no React and no
// store, so the arithmetic can be tested on its own.
//
// Three things here are deliberate departures from what the univariate page has
// always done, and each fixes a defect rather than restyling one:
//
// 1. The denominator is the node's own population, taken from the tree, never
//    the sum of the buckets. Summing is correct only when buckets partition the
//    node. A multi-valued trait like `tags` puts a lead in several buckets at
//    once, so on one real node its 51 values summed to 19,500 leads against a
//    node of 3,698 and every lift on the page was measured against a 4.99%
//    baseline that belonged to no node at all. Taking the node's figures also
//    stops the "include unknown values" toggle from moving the baseline, which
//    it silently did on every attribute.
// 2. A bucket is compared with the REST of the node, not with the whole of it.
//    A bucket holding 42% of the node is 42% of any baseline that includes it,
//    so comparing it with the whole compares it partly with itself. The
//    complement is also exactly the split being proposed, which is why the old
//    `lift 7 factor` column was computing this quantity all along.
// 3. Lift is a multiple of the baseline, `1.14x`, matching the tree page and the
//    model summary. The univariate page was the only surface using
//    `(rate - base) / base`, where parity reads as 0 rather than 1.

/** Both children of a split need this many rows. `MIN_SPLIT_POPULATION`. */
import {
  declaredOrder,
  exclusionReason,
  isMultiValued,
  isUnordered,
  nullConflatedWith,
} from './computationDictionary';

export const MIN_SPLIT_POPULATION = 100;
/** A split below this gain is refused. `MIN_INFORMATION_GAIN`. */
export const MIN_INFORMATION_GAIN = 0.003;
/**
 * The shallowest node at which a column that stores missing data as a real value may be
 * split on at all. The root is 0, so 0 and 1 are the top of the tree.
 *
 * These columns cannot be cleaned. On staging 426132 model 53 node 16, fifteen unrelated
 * technology categories each showed a non-zero side converting 3 to 5 times the node, and
 * their ZERO sides all sat between 11.6% and 15.3% against a 15.6% baseline, because they
 * are largely the same 335 companies: the ones HG has no coverage for.
 *
 * The split is taken anyway once the tree is past its top, deliberately. Some of that lift
 * is real, a bigger technology estate does correlate with fit, and nothing separates the
 * two. What is not acceptable is near the root, where the cut divides the WHOLE model by
 * data availability and every branch below inherits a population defined by what could be
 * enriched rather than by what the companies are like.
 *
 * Kept identical to `COVERAGE_SPLIT_MIN_DEPTH` in rgip-copilot's `nodeinsights.py`.
 */
export const COVERAGE_SPLIT_MIN_DEPTH = 2;
/** Buckets are only called different from the rest below this. */
export const ALPHA = 0.05;
/**
 * Above this many values an attribute gets no split candidates.
 *
 * Candidate building is quadratic in the value count, and the panel runs it for every
 * attribute in the node. A raw, unbucketed number has one value per distinct figure: on a
 * 58k-lead root node `snowflake_annual_revenue` carried 23,265, and 20k values alone cost
 * 3.6 GB, which killed the browser tab without a console error. At 1,000 it costs ~5 ms.
 * A split cut by conversion rate across that many values would not mean anything anyway.
 */
export const MAX_SPLIT_VALUES = 1000;

const Z = 1.96;

export interface RawBucket {
  population: number;
  conversions: number;
  rawName: string;
  name: string;
}

/** The node's own figures, from the tree structure rather than from the buckets. */
export interface NodeTotals {
  population: number;
  conversions: number;
}

export interface BucketStat {
  name: string;
  rawName: string;
  population: number;
  conversions: number;
  rate: number;
  /** Rate over the node's baseline, so parity is 1. */
  lift: number;
  /** Rate over the rest of the node's rate. This is the split being proposed. */
  liftVsRest: number;
  /** Wilson bounds on this bucket's own rate, for the tooltip. */
  low: number;
  high: number;
  /** Two-proportion p value against the rest of the node. */
  p: number;
  /** Whether the difference from the rest survives whichever test is in force. */
  clears: boolean;
  populationShare: number;
  conversionsShare: number;
  /** Either side of the implied split would be under the minimum. */
  tooSmall: boolean;
}

export interface SplitCandidate {
  /** Index into the rate-sorted rows this candidate is anchored to. */
  index: number;
  /** Human-readable rule for the matched side. */
  label: string;
  /** Human-readable rule for the other side. */
  otherLabel: string;
  /**
   * Indexes into `rows` of the buckets on the matched side. "Prospect or
   * better" says nothing about which buckets that actually is, so the panel
   * uses this to light them up in the table underneath.
   */
  matchedRows: number[];
  /** Raw bucket values for the matched side, ready for a split condition. */
  matchedValues: string[];
  /** True when the rule is an absence, so the condition is "is none of". */
  negated: boolean;
  matched: { population: number; conversions: number; rate: number };
  rest: { population: number; conversions: number; rate: number };
  gain: number;
  /** Gain as a share of the node's entropy, which is comparable across nodes. */
  normalisedGain: number;
  populationOk: boolean;
  gainOk: boolean;
  /** The matched side actually converts better than the node it came from. */
  beatsNode: boolean;
  /**
   * The matched side contains the bucket this column stores missing data in.
   *
   * A cut like that scores "we know nothing about this company" as a reason to rate it
   * highly, and the better it performs the more certainly it is measuring how much of
   * your data HG could enrich rather than anything about the companies. It can hide in
   * either direction: `count in (0 and below)` names the bucket, `tech is not Salesforce`
   * does not and still puts it on the positive branch.
   */
  noDataOnPositiveSide: boolean;
  legal: boolean;
}

export type AttributeShape = 'partition' | 'multi' | 'ordinal';

export interface AttributeStats {
  rows: BucketStat[];
  node: NodeTotals;
  baseline: number;
  entropy: number;
  shape: AttributeShape;
  /**
   * Why this column is not a model feature, when the dictionary says so.
   *
   * The server already drops 145 columns before the panel sees them
   * (`univariateVariablesToIgnore`), but that list is exact-match on the subject, so a
   * duplicate of a column we keep, an outcome-derived field or an enrichment-status column
   * still arrives. The distribution is worth looking at; a split on it is not, and saying
   * "no split clears the minimums" about it is false.
   */
  ineligible?: string;
  /**
   * Why this column yields nothing AT THIS NODE, although it is a real feature elsewhere.
   *
   * Distinct from `ineligible` on purpose: that one is a property of the column and holds
   * everywhere, this one is a property of where in the tree you are standing, and telling
   * somebody a field is "not a field to split on" when it is fine two levels down sends
   * them to look for a problem with the field.
   */
  ineligibleHere?: string;
  /** The value count, set only when it is above `MAX_SPLIT_VALUES` and nothing is proposed. */
  tooManyValues?: number;
  /** Buckets summed over the node's population. Above 1 means values overlap. */
  coverage: number;
  /** Average number of values carried per lead, only meaningful when multi. */
  valuesPerLead: number;
  candidates: SplitCandidate[];
  best: SplitCandidate | null;
  legalCount: number;
  flaggedCount: number;
  /** A bucket larger than the node itself, which is impossible for real node data. */
  exceedsNode: boolean;
}

export interface AnalyseOptions {
  /** Hide buckets whose name contains "unknown". Matches the existing toggle. */
  hideUnknown?: boolean;
  /**
   * Control the false discovery rate across an attribute's buckets rather than
   * testing each at 5%. On a 51-value attribute a flat 5% flags nine buckets
   * where chance alone produces about two and a half of them.
   */
  correctForMultipleTests?: boolean;
  /**
   * The column these buckets belong to, so the computation dictionary can be consulted.
   *
   * Optional because a caller that does not supply it gets exactly today's behaviour:
   * every lookup answers "unknown" and the shape falls back to the coverage heuristic.
   */
  column?: string;
  /**
   * How far this node sits from the root, the root being 0.
   *
   * Only the coverage rule reads it. Leave it out and that rule does not fire: an absent
   * depth must never read as 0, or a caller with no tree to measure against silently
   * loses every column that conflates missing data.
   */
  depth?: number;
}

/** Standard normal CDF, Zelen and Severo. Ample for a 0.05 cutoff. */
export function normalCdf(z: number): number {
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989422804014327 * Math.exp((-z * z) / 2);
  const p =
    d *
    t *
    (0.31938153 +
      t *
        (-0.356563782 +
          t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  return z > 0 ? 1 - p : p;
}

/**
 * `1 - ]0,1E6]` -> `]0,1E6]`, `0 - null` -> `null`, `Software - Other` -> `Software - Other`.
 *
 * Studio's bucketing computations number their CASE branches in scale order and Studio
 * shows the number, so a bucket arrives with the author's own position glued to the front
 * of the level name. The dictionary declares the levels as the computation writes them,
 * WITHOUT the number, so matching the two needs this off first.
 *
 * Not cosmetic: without it the declared order matched no bucket on 95 of the 103 ordered
 * columns on staging 426132/53/16, every lookup missed, and the sort fell through to rate
 * order while still labelling its output as a band. That is worse than not knowing the
 * order, because the result claims to be a contiguous run of a scale and is not.
 *
 * Only a leading NUMBER counts. Splitting on the last (or any) " - " truncates
 * `Software - Other`, a real value of `hg__tech_category`, to `Other`, which the
 * unknown-bucket predicate then matches, silently dropping a 19-record category.
 *
 * Mirrors `_ORDINAL_PREFIX` in rgip-copilot's `nodeinsights.py`.
 */
const ORDINAL_PREFIX = /^\s*\d+(?:\.\d+)?\s+-\s+([\s\S]*)$/;

export function levelName(name: string): string {
  const match = ORDINAL_PREFIX.exec(name);
  return match ? match[1].trim() : name;
}

/**
 * Wilson score interval. Chosen over the normal approximation because the rows
 * that decide a split are routinely small or near 0 or 1, which is exactly where
 * the approximation fails: 41 conversions out of 42 gives a Wald interval that
 * runs past 100%.
 */
export function wilsonInterval(
  conversions: number,
  population: number
): [number, number] {
  if (!population) return [0, 0];
  const p = conversions / population;
  const denom = 1 + (Z * Z) / population;
  const centre = p + (Z * Z) / (2 * population);
  const radius =
    Z *
    Math.sqrt(
      (p * (1 - p)) / population + (Z * Z) / (4 * population * population)
    );
  return [
    Math.max(0, (centre - radius) / denom),
    Math.min(1, (centre + radius) / denom),
  ];
}

/** Two-sided p value for two proportions differing, pooled variance. */
export function twoProportionP(
  c1: number,
  n1: number,
  c2: number,
  n2: number
): number {
  if (!n1 || !n2) return 1;
  const pooled = (c1 + c2) / (n1 + n2);
  const se = Math.sqrt(pooled * (1 - pooled) * (1 / n1 + 1 / n2));
  if (!se) return 1;
  const z = (c1 / n1 - c2 / n2) / se;
  return 2 * (1 - normalCdf(Math.abs(z)));
}

/** Binary entropy in bits. Zero for a pure or empty group. */
export function entropy(conversions: number, population: number): number {
  if (!population) return 0;
  const p = conversions / population;
  if (p <= 0 || p >= 1) return 0;
  return -(p * Math.log2(p) + (1 - p) * Math.log2(1 - p));
}

export function isUnknownBucket(name?: string): boolean {
  return !!name && name.toLowerCase().includes('unknown');
}

/**
 * Benjamini and Hochberg, returning the set of rows whose difference survives.
 * Controls the share of flagged rows that are false, which is the question
 * someone scanning an attribute actually has, rather than the chance of any
 * single false flag.
 */
function survivingBenjaminiHochberg(rows: BucketStat[]): Set<BucketStat> {
  const byP = [...rows].sort((a, b) => a.p - b.p);
  let cut = 0;
  byP.forEach((row, i) => {
    if (row.p <= ((i + 1) / byP.length) * ALPHA) cut = i + 1;
  });
  return new Set(byP.slice(0, cut));
}

export function analyseAttribute(
  buckets: RawBucket[],
  node: NodeTotals,
  options: AnalyseOptions = {}
): AttributeStats {
  const { population: nodePopulation, conversions: nodeConversions } = node;
  const baseline = nodePopulation ? nodeConversions / nodePopulation : 0;
  const nodeEntropy = entropy(nodeConversions, nodePopulation);

  // Coverage is measured over every bucket, including the hidden ones: whether
  // an attribute's values overlap is a property of the attribute, not of a
  // display toggle.
  const allPopulation = buckets.reduce(
    (sum, b) => sum + Number(b.population),
    0
  );
  const coverage = nodePopulation ? allPopulation / nodePopulation : 0;
  const column = options.column ?? '';
  const order = column ? declaredOrder(column) : undefined;
  // Declared beats inferred, in that order. An ordered scale has to be cut along its own
  // sequence, so it is its own shape. Otherwise the dictionary states whether a record can
  // carry several values, and only when it does not know does the coverage heuristic run:
  // that threshold is a fudge (474 of 511 variables on one staging node totalled 1,507
  // against a node of 1,528, so exact equality classified almost everything as sparse) and
  // getting it wrong compared a serialised array to one element and applied a split with
  // 0 leads and 0 conversions on one side.
  const declaredMulti = column ? isMultiValued(column) : undefined;
  const multi = declaredMulti ?? coverage > 1.01;
  const shape: AttributeShape =
    order !== undefined && !multi ? 'ordinal' : multi ? 'multi' : 'partition';

  // A single bucket cannot be larger than the node it sits in. When one is, the
  // endpoint has served whole-model statistics under a node heading, which it
  // does with an HTTP 200 after a tree write, so nothing else would notice.
  const exceedsNode = buckets.some(
    (b) => Number(b.population) > nodePopulation
  );

  const visible =
    options.hideUnknown && buckets.length > 1
      ? buckets.filter((b) => !isUnknownBucket(b.name))
      : buckets;

  const rows: BucketStat[] = visible.map((bucket) => {
    const population = Number(bucket.population);
    const conversions = Number(bucket.conversions);
    const rate = population ? conversions / population : 0;
    const restPopulation = nodePopulation - population;
    const restConversions = nodeConversions - conversions;
    const restRate = restPopulation > 0 ? restConversions / restPopulation : 0;
    const [low, high] = wilsonInterval(conversions, population);
    return {
      name: bucket.name,
      rawName: bucket.rawName,
      population,
      conversions,
      rate,
      lift: baseline ? rate / baseline : 0,
      liftVsRest: restRate ? rate / restRate : 0,
      low,
      high,
      p: twoProportionP(
        conversions,
        population,
        restConversions,
        restPopulation
      ),
      clears: false,
      populationShare: nodePopulation ? population / nodePopulation : 0,
      conversionsShare: nodeConversions ? conversions / nodeConversions : 0,
      tooSmall:
        population < MIN_SPLIT_POPULATION ||
        restPopulation < MIN_SPLIT_POPULATION,
    };
  });

  // Sorted by rate so that, for a partitioning attribute, every gap between two
  // rows is a candidate split. Alphabetical order is the display equivalent of
  // no analysis.
  //
  // An ORDERED scale is the exception, and sorting it by rate is what made a "candidate"
  // like "IT spend is LOW or VHI" possible: a set nobody can act on or defend, fitted to
  // whichever bands happened to convert in training. Its own sequence is the only order in
  // which a cut of it means anything, so it is sorted by declared position and the rows
  // are rendered that way too.
  if (shape === 'ordinal' && order) {
    const position = new Map(order.map((level, index) => [level, index]));
    rows.sort((a, b) => {
      const ai = position.get(levelName(a.name));
      const bi = position.get(levelName(b.name));
      // A level the dictionary does not name (an unknown bucket, most often) sorts to the
      // end rather than to an arbitrary point inside the scale.
      if (ai === undefined && bi === undefined) return b.rate - a.rate;
      if (ai === undefined) return 1;
      if (bi === undefined) return -1;
      return ai - bi;
    });
  } else {
    rows.sort((a, b) => b.rate - a.rate);
  }

  if (options.correctForMultipleTests) {
    const survivors = survivingBenjaminiHochberg(rows);
    rows.forEach((row) => {
      row.clears = survivors.has(row);
    });
  } else {
    rows.forEach((row) => {
      row.clears = row.p < ALPHA;
    });
  }

  // The value this column stores missing data in, and only when it is actually one of
  // THIS node's buckets. `tech` and `tags` conflate to the empty string, which the
  // univariate never reports as a bucket, so there is no no-data group here to keep off
  // the positive side. Scoped by the declaration alone the rule refused every negated
  // technology candidate on the node.
  //
  // Matched on `rawName`, because `hg__has_crm_tech` shows `Yes` and `No` while holding
  // `1` and `0`, and comparing against the display name misses the whole flag family.
  const declaredNull = column ? nullConflatedWith(column) : undefined;
  const conflatedNull =
    declaredNull !== undefined &&
    buckets.some((b) => b.rawName === declaredNull)
      ? declaredNull
      : undefined;

  // A column that is not a model feature yields no candidates at all. Not an early
  // return: the rail ranks every attribute and indexes into the list, so the shape of the
  // answer has to stay the same and only the candidates go.
  const ineligible = column ? exclusionReason(column) : undefined;
  const ineligibleHere =
    !ineligible &&
    conflatedNull !== undefined &&
    options.depth !== undefined &&
    options.depth < COVERAGE_SPLIT_MIN_DEPTH
      ? 'it stores "no data" as a real value, so this close to the root a split on it ' +
        'divides the whole model by what could be enriched rather than by what the ' +
        'companies are like'
      : undefined;
  const valueCount = rows.filter((row) => !isUnknownBucket(row.name)).length;
  const tooManyValues =
    !ineligible && !ineligibleHere && valueCount > MAX_SPLIT_VALUES
      ? valueCount
      : undefined;
  const candidates =
    ineligible || ineligibleHere || tooManyValues
      ? []
      : buildCandidates(rows, node, shape, nodeEntropy, column, conflatedNull);
  const legal = candidates.filter((c) => c.legal);
  const best = legal.length
    ? legal.reduce((a, b) => (b.gain > a.gain ? b : a))
    : null;

  return {
    rows,
    node,
    baseline,
    ineligible,
    ineligibleHere,
    tooManyValues,
    entropy: nodeEntropy,
    shape,
    coverage,
    valuesPerLead: coverage,
    candidates,
    best,
    legalCount: legal.length,
    flaggedCount: rows.filter((r) => r.clears).length,
    exceedsNode,
  };
}

/**
 * The matched side of a partition candidate, said the way its values allow.
 *
 * The rows are sorted by conversion RATE, so a prefix of them is "the best-converting N".
 * `X or better` states that as if the values were ranked, which is true of a scale and
 * false of a set: "Software vSMB or better" invites the reader to believe Software Ent
 * outranks TelCo by something other than how they happened to convert here. Where the
 * dictionary says the values are unordered, the cut is named by listing them.
 *
 * Long lists are truncated rather than wrapped: past three names the reader is counting,
 * not reading, and the full membership is on the ladder underneath.
 */
function partitionLabel(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length <= 3) {
    return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
  }
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
}

/**
 * The rest of a candidate is always the node minus the matched side, never the
 * sum of the remaining buckets. That is what makes one code path serve both
 * shapes, and it puts any hidden bucket on the negative side where it belongs
 * rather than dropping it out of the arithmetic.
 */
function buildCandidates(
  rows: BucketStat[],
  node: NodeTotals,
  shape: AttributeShape,
  nodeEntropy: number,
  column = '',
  conflatedNull?: string
): SplitCandidate[] {
  const baseline = node.population ? node.conversions / node.population : 0;
  const make = (
    index: number,
    label: string,
    otherLabel: string,
    population: number,
    conversions: number,
    matchedRows: number[],
    matchedValues: string[],
    negated = false
  ): SplitCandidate => {
    const restPopulation = node.population - population;
    const restConversions = node.conversions - conversions;
    const gain =
      nodeEntropy -
      ((population / node.population) * entropy(conversions, population) +
        (restPopulation / node.population) *
          entropy(restConversions, restPopulation));
    const populationOk =
      Math.min(population, restPopulation) >= MIN_SPLIT_POPULATION;
    const gainOk = gain >= MIN_INFORMATION_GAIN;
    // The engine requires the matched side to beat the node's own rate. Without
    // it the highest-gain candidate is often one whose matched side is the
    // worse half, which reads as a recommendation to split towards the bad
    // leads. It also filters out the case where all the gain comes from a
    // hidden unknown bucket stranded on the negative side: a node whose
    // unknowns convert at 83% against 10% everywhere else is a data-absence
    // signal to investigate, not a split to build.
    const beatsNode = population > 0 && conversions / population > baseline;
    // Which side the no-data bucket lands on. `negated` flips the question: the matched
    // side of "is none of X" is everything the list does NOT name, so the bucket is on it
    // precisely when this candidate is not the one calling it out.
    //
    // Not covered by `beatsNode`, which only asks whether the matched side converts above
    // the node. It says nothing about a side that clears the bar WHILE carrying the
    // no-data group along inside it, which is the common shape: a bottom-anchored band
    // starting at the zero bucket, or any negation on a multi-valued column.
    const noDataOnPositiveSide =
      conflatedNull !== undefined &&
      (negated
        ? !matchedValues.includes(conflatedNull)
        : matchedValues.includes(conflatedNull));
    return {
      index,
      label,
      otherLabel,
      matchedRows,
      matchedValues,
      negated,
      matched: {
        population,
        conversions,
        rate: population ? conversions / population : 0,
      },
      rest: {
        population: restPopulation,
        conversions: restConversions,
        rate: restPopulation > 0 ? restConversions / restPopulation : 0,
      },
      gain,
      normalisedGain: nodeEntropy ? gain / nodeEntropy : 0,
      populationOk,
      gainOk,
      beatsNode,
      noDataOnPositiveSide,
      legal: populationOk && gainOk && beatsNode && !noDataOnPositiveSide,
    };
  };

  // A multi-valued attribute cannot be cut along the sorted order: a lead
  // tagged both `SAAS` and `Finance` would land on both sides. The only legal
  // split is a membership test, so every value is its own candidate.
  if (shape === 'multi') {
    return rows
      .filter((row) => !isUnknownBucket(row.name))
      .map((row) => {
        // A membership test and its negation are the same split, so the
        // candidate is built in whichever direction puts the better converting
        // side on the positive branch. Without this, an attribute whose good
        // signal is an absence ("not tagged Technology" converts at 7.7%
        // against 4.5%) reads as having no legal split at all.
        const restPopulation = node.population - row.population;
        const restConversions = node.conversions - row.conversions;
        const restRate =
          restPopulation > 0 ? restConversions / restPopulation : 0;
        const index = rows.indexOf(row);
        // When the rule is the absence, the matched side is every other row,
        // and the condition the tree needs is "is none of" on this one value.
        const others = rows.map((_, i) => i).filter((i) => i !== index);
        return row.rate >= restRate
          ? make(
              index,
              row.name,
              `not ${row.name}`,
              row.population,
              row.conversions,
              [index],
              [row.rawName]
            )
          : make(
              index,
              `not ${row.name}`,
              row.name,
              restPopulation,
              restConversions,
              others,
              [row.rawName],
              true
            );
      });
  }

  // An ORDERED scale is cut along its own sequence, never by picking the best-converting
  // subset. `rows` arrives in declared order, so a prefix and a suffix are the only two
  // families of contiguous cut, and both ends are offered: a size or maturity rule belongs
  // at an end of the scale, and a window cut from the middle reads as arbitrary even though
  // it is technically contiguous.
  //
  // Deliberately never the whole scale. A run covering every level does not cut it at all,
  // it separates records that HAVE a value from those that do not, which scores enrichment
  // coverage rather than fit.
  if (shape === 'ordinal') {
    const levels = rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => !isUnknownBucket(row.name));
    const ordinalCandidates: SplitCandidate[] = [];
    for (let size = 1; size < levels.length; size += 1) {
      const bottom = levels.slice(0, size);
      const top = levels.slice(levels.length - size);
      const push = (
        run: typeof levels,
        label: string,
        otherLabel: string
      ): void => {
        const population = run.reduce((sum, l) => sum + l.row.population, 0);
        const conversions = run.reduce((sum, l) => sum + l.row.conversions, 0);
        if (population >= node.population) return;
        ordinalCandidates.push(
          make(
            run[run.length - 1].index,
            label,
            otherLabel,
            population,
            conversions,
            run.map((l) => l.index),
            run.map((l) => l.row.rawName)
          )
        );
      };
      // The level's own name, not the bucket's. A bucket arrives as `0 - 0` or
      // `1 - ]0,1E6]`, and "0 - 0 and below" reads as a range that has been cut in half.
      const lowest = levelName(bottom[bottom.length - 1].row.name);
      const highest = levelName(top[0].row.name);
      push(bottom, `${lowest} and below`, `above ${lowest}`);
      push(top, `${highest} and above`, `below ${highest}`);
    }
    return ordinalCandidates;
  }

  // An unknown bucket never joins the matched side, whatever its rate, because
  // a tree that gates on absent data scores enrichment coverage rather than
  // fit. The engine enforces this, so a candidate the panel proposes with
  // unknown on the positive side is one the engine would refuse to build.
  //
  // It is not a formality. On a real node the unknown industry bucket converts
  // at 10.6% against a 6.5% baseline, second best on the attribute. Allowing it
  // across yields three legal cuts; holding the rule yields none, because every
  // cut then strands a genuinely good group on the negative side. The honest
  // answer is that the attribute has no legal split, and an unknown bucket
  // converting well above baseline is itself worth investigating.
  // "or better" reads a rate ranking as if it were the values' own order. That is fair
  // for a scale and false for a set, so it survives only where nothing says otherwise:
  // an unknown column, or a numeric, where an order does exist even undeclared.
  const unordered = column ? isUnordered(column) : false;
  const candidates: SplitCandidate[] = [];
  const matchedRows: number[] = [];
  const matchedValues: string[] = [];
  let population = 0;
  let conversions = 0;
  rows.forEach((row, i) => {
    if (isUnknownBucket(row.name)) return;
    population += row.population;
    conversions += row.conversions;
    matchedRows.push(i);
    matchedValues.push(row.rawName);
    // A cut that leaves nothing on the other side is not a split. Only the
    // node's own totals can decide that, since hidden buckets still count.
    if (population >= node.population) return;
    candidates.push(
      make(
        i,
        unordered
          ? partitionLabel(matchedRows.map((index) => rows[index].name))
          : `${row.name} or better`,
        'everything else',
        population,
        conversions,
        [...matchedRows],
        [...matchedValues]
      )
    );
  });
  return candidates;
}

/** How strongly an attribute should rank in the list, best legal split first. */
export function attributeScore(stats: AttributeStats): number {
  return stats.best ? stats.best.normalisedGain : 0;
}

export interface RolledUpRows {
  count: number;
  largestShare: number;
  lowestRate: number;
  highestRate: number;
}

export type LadderItem =
  | { kind: 'row'; row: BucketStat }
  | { kind: 'rollup'; rollup: RolledUpRows };

/**
 * Long attributes are a scroll rather than a read, so the middle rolls up. Two
 * kinds of row are never rolled up whatever their rank: one that is a legal
 * split, and one whose difference from the rest is real. Without that rule the
 * rollup is a silent cap on the only actionable rows, and it bites immediately:
 * on a real `tags` attribute the four splittable values sat mid-table at 4.5% to
 * 4.8%, so a plain top-and-bottom rollup hid every one of them.
 */
export function ladderItems(
  stats: AttributeStats,
  expanded: boolean,
  maxRows = 14
): LadderItem[] {
  const { rows } = stats;
  if (expanded || rows.length <= maxRows) {
    return rows.map((row) => ({ kind: 'row' as const, row }));
  }

  const keep = new Set<BucketStat>();
  stats.candidates.forEach((c) => {
    if (c.legal && rows[c.index]) keep.add(rows[c.index]);
  });
  rows.forEach((row) => {
    if (row.clears) keep.add(row);
  });

  const items: LadderItem[] = [];
  let run: BucketStat[] = [];
  const flush = () => {
    if (!run.length) return;
    if (run.length === 1) {
      items.push({ kind: 'row', row: run[0] });
    } else {
      items.push({
        kind: 'rollup',
        rollup: {
          count: run.length,
          largestShare: Math.max(...run.map((r) => r.populationShare)),
          lowestRate: Math.min(...run.map((r) => r.rate)),
          highestRate: Math.max(...run.map((r) => r.rate)),
        },
      });
    }
    run = [];
  };

  rows.forEach((row, i) => {
    const anchored = i < 6 || i >= rows.length - 4 || keep.has(row);
    if (anchored) {
      flush();
      items.push({ kind: 'row', row });
    } else {
      run.push(row);
    }
  });
  flush();
  return items;
}
